// =============================================================================
// selectors/reconcile.ts — the reconciliation, per IMPORT BATCH.
// -----------------------------------------------------------------------------
// `domain/reconcile.ts` answers "which sources recorded this account-day, and do
// they agree". Import History asks a narrower question: **if I undo THIS import,
// what do I lose?**
//
// 🚨 THE ANSWER IS NOT "IS IT REDUNDANT ON THE DAYS IT SHARES". A batch spans
// days, and a day nobody else recorded is the reason a batch must be kept — so
// the verdict has to be computed over EVERY day the batch touches, not only the
// contested ones. A version that reconciled the shared days alone would have
// called Josh's Sierra file removable on the strength of one overlapping session
// while it was the sole record of sixteen MES round trips.
//
// ⚠ `ui/` may not import `domain/`, so the types and helpers travel through
// here — same rule as `selectors/excursionStats.ts`.
// =============================================================================

export {
  LIVE_SOURCE,
  findMultiSourceDays,
  groupIntoPositions,
  reconcileAccountDay,
} from '../domain/reconcile';

export type {
  DirectionConflict,
  Position,
  ReconcileReport,
  ReconcileTrade,
  Redundancy,
  SourcePair,
  SourceSummary,
} from '../domain/reconcile';

import {
  reconcileAccountDay, type DirectionConflict, type ReconcileTrade,
} from '../domain/reconcile';

/** What undoing one import would cost, and what it would fix. */
export interface BatchVerdict {
  batchId: string;
  /** Account-days this batch wrote to. */
  days: number;
  /** …of those, how many another source also recorded. */
  sharedDays: number;
  /**
   * Positions this batch holds that exist in NO other source.
   *
   * 🚨 THE NUMBER THAT DECIDES IT. Zero means undoing the import is provably
   * lossless. Anything else is exactly what would be destroyed.
   */
  uniquePositions: number;
  /** Trades in those unique positions — the count the user recognises. */
  uniqueTrades: number;
  /** Zero unique positions, and it shares at least one day with something. */
  fullyDuplicated: boolean;
  /**
   * Instruments where this batch disagrees with another source about DIRECTION.
   *
   * ⚠ Not a reason to remove it — a reason to LOOK. On 2026-08-05 Sierra showed
   * zero MNQ longs on a day the logger showed two, and it was Sierra that had
   * mis-paired the fills; but the same signal with the sources swapped would
   * mean the opposite.
   */
  conflicts: DirectionConflict[];
}

const dayKey = (t: ReconcileTrade): string => `${t.account}|${t.date}`;

/**
 * A verdict for every import batch present in `trades`.
 *
 * ⚠ Days are reconciled ONCE and shared between batches. A book with twenty
 * imports over the same fortnight would otherwise re-walk each day twenty times.
 */
export function batchVerdicts(trades: readonly ReconcileTrade[]): Map<string, BatchVerdict> {
  const byDay = new Map<string, ReconcileTrade[]>();
  for (const t of trades) {
    const k = dayKey(t);
    const list = byDay.get(k);
    if (list) list.push(t); else byDay.set(k, [t]);
  }

  const out = new Map<string, BatchVerdict>();
  const get = (id: string): BatchVerdict => {
    let v = out.get(id);
    if (!v) {
      v = {
        batchId: id, days: 0, sharedDays: 0, uniquePositions: 0,
        uniqueTrades: 0, fullyDuplicated: false, conflicts: [],
      };
      out.set(id, v);
    }
    return v;
  };

  for (const dayTrades of byDay.values()) {
    const sources = new Set(dayTrades.map((t) => t.sourceKey));
    const report = sources.size > 1 ? reconcileAccountDay(dayTrades) : null;

    for (const key of sources) {
      const v = get(key);
      v.days++;

      if (!report) {
        // ⚠ SOLE SOURCE FOR THIS DAY. Every position it holds is unique by
        // definition — nothing else recorded the day at all — so this is the
        // case that makes a batch un-removable, and it is invisible to any
        // reconciliation that only looks at contested days.
        const mine = dayTrades.filter((t) => t.sourceKey === key);
        const positions = new Set(mine.map((t) => `${t.contract}|${t.side}|${t.timeIn}`));
        v.uniquePositions += positions.size;
        v.uniqueTrades += mine.length;
        continue;
      }

      v.sharedDays++;
      const red = report.redundancy.find((r) => r.key === key);
      if (red) {
        v.uniquePositions += red.unique.length;
        v.uniqueTrades += red.unique.reduce((n, p) => n + p.tradeIds.length, 0);
      }
      for (const c of report.directionConflicts) {
        if (c.key === key || c.againstKey === key) v.conflicts.push(c);
      }
    }
  }

  for (const v of out.values()) {
    v.fullyDuplicated = v.uniquePositions === 0 && v.sharedDays > 0;
  }
  return out;
}
